import type {
  DropdownLinkList,
  FooterNavigationItemLink,
  FooterNavigationItemText,
  SocialLinks,
} from "@homeinstead/schemas";
import type { PortableTextBlock } from "next-sanity";
import type { FooterColumnContentProps } from "../../../types/footer-columns";

import { RichText } from "../../RichText";
import { FooterLink } from "../FooterLink";
import { FooterSocialIcons } from "./FooterSocialIcons";
import { LinkSelect } from "./LinkSelect";
import { mapOfficeSocialMediaLinksToFooterLinks } from "./map-social-media-links";

type FooterColumnContentItem = FooterColumnContentProps & {
  [key: string]: unknown;
};

function isSocialLinks(
  item: FooterColumnContentItem,
): item is SocialLinks & Pick<FooterColumnContentProps, "_key" | "type"> {
  return item.type === "socialLinks";
}

const FooterColumnContent = ({
  placeholders,
  office,
  ...item
}: FooterColumnContentProps & { [key: string]: unknown }) => {
  if (item.type === "footerNavigationItemText") {
    const { content } = item as unknown as FooterNavigationItemText;

    return (
      <RichText
        placeholders={placeholders}
        value={content as PortableTextBlock[]}
      />
    );
  }

  if (item.type === "footerNavigationItemLink") {
    const { label, fullPath, openInNewTab } =
      item as unknown as FooterNavigationItemLink & { fullPath: string | null };

    if (!fullPath) {
      return null;
    }

    return (
      <div className="block">
        <FooterLink
          href={fullPath}
          target={openInNewTab ? "_blank" : undefined}
        >
          {label}
        </FooterLink>
      </div>
    );
  }

  if (item.type === "dropdownLinkList") {
    const { items } = item as unknown as DropdownLinkList & {
      items: (DropdownLinkList["items"][number] & {
        fullPath: string | null;
        iconUrl?: string | null;
      })[];
    };

    return (
      <div className="block max-w-[200px]">
        <LinkSelect items={items} />
      </div>
    );
  }

  if (isSocialLinks(item)) {
    // Use office root-level URLs if available, otherwise fall back to item links
    const officeLinks = mapOfficeSocialMediaLinksToFooterLinks(office);
    return (
      <FooterSocialIcons
        links={{
          facebook: officeLinks.facebook ?? item.facebook,
          instagram: officeLinks.instagram ?? item.instagram,
          linkedin: officeLinks.linkedin ?? item.linkedin,
          tiktok: officeLinks.tiktok ?? item.tiktok,
          youtube: officeLinks.youtube ?? item.youtube,
        }}
      />
    );
  }

  return null;
};

export { FooterColumnContent };
